import { forwardRef, useImperativeHandle, useState } from 'react';
import { IconTaskNew } from 'components/Icons';
import { AskRequestCreateType, AskRequestStatus, CreateAskRequest } from '@hone-automation/common';
import { useAuthContext } from 'context/useAuthContext';
import { useLocationsStore } from 'hooks/useLocationsStore';
import CMComposer from '../../Queue/CMComposer/CMComposer';
import { CMComposerContexts } from 'presentation/components/Queue/CMComposer/types';
import KSCheckBox from 'presentation/components/KSCheckBox';
import KSSelector from 'presentation/components/KSSelector';
import KSInputField from 'presentation/components/KSInputField';
import { CreateModal } from '../CreateModal';

export interface CreateRequestModalProps {
  isOpen: boolean;
  handleClose: () => void;
  onSubmit: (data: CreateAskRequest) => void;
  resetState: () => void;
  locationId?: string;
  title?: string;
}

export interface CreateRequestModalRef {
  resetState: () => void;
}

export const CreateRequestModal = forwardRef<CreateRequestModalRef, CreateRequestModalProps>((props, ref) => {
  const locationId = props.locationId;
  const { isBookkeeperAdmin } = useAuthContext();

  // Resolve location name from global locations store
  const allLocations = useLocationsStore(state => state.allLocations);
  const matchedLocation = locationId
    ? allLocations.find(loc => loc.id === locationId || (loc as any).locationId === locationId)
    : undefined;
  const locationName =
    (matchedLocation && ((matchedLocation as any).locationName || (matchedLocation as any).name)) || '';
  const title = props.title || 'New Ask Request';

  // Local state for form fields
  const [identifier, setIdentifier] = useState('');
  const [forValue, setForValue] = useState<AskRequestStatus>(isBookkeeperAdmin ? 'Open' : 'In Progress');
  const [isInternal, setIsInternal] = useState(false);

  useImperativeHandle(ref, () => ({
    resetState: () => {
      setIdentifier('');
      setForValue(isBookkeeperAdmin ? 'Open' : 'In Progress');
      setIsInternal(false);
    },
  }));

  const onSubmit = (formData: FormData, createType: AskRequestCreateType) => {
    formData.append('identifier', identifier);
    formData.append('createType', createType);
    formData.append('type', 'Customer Request');
    props.onSubmit(formData as any);
  };

  const assignOptions = [
    { label: 'Customer', value: 'Open' },
    { label: 'Accountant', value: 'In Progress' },
  ];

  return (
    <CreateModal
      isOpen={props.isOpen}
      onClose={props.handleClose}
      title={title}
      subtitle={locationName}
      icon={<IconTaskNew />}
      isInternal={isInternal}
      footer={
        <CMComposer
          handleOnCallback={onSubmit}
          hideReopenLabel
          sendType={forValue === 'Open' ? 'For Customer' : 'For Accountant'}
          context={CMComposerContexts.createRequest}
          isCreateMode
          isInternal={isInternal}
        />
      }
    >
      <CreateModal.FormRow label="Title">
        <KSInputField
          value={identifier}
          placeholder="New request title"
          onChange={e => setIdentifier(e.target.value)}
        />
      </CreateModal.FormRow>

      {isBookkeeperAdmin && (
        <>
          <CreateModal.FormRow label="Assign to">
            <KSSelector
              options={assignOptions}
              value={forValue}
              onChange={option => {
                setForValue(option as AskRequestStatus);
                // Reset internal when switching to Customer
                if (option === 'Open') {
                  setIsInternal(false);
                }
              }}
            />
          </CreateModal.FormRow>

          {forValue !== 'Open' && (
            <CreateModal.FormFrame>
              <KSCheckBox
                rounded
                label="Internal (Visible to KS Only)"
                checked={isInternal}
                onChange={checked => setIsInternal(checked)}
                colorLabel="gray"
              />
            </CreateModal.FormFrame>
          )}
        </>
      )}
    </CreateModal>
  );
});

CreateRequestModal.displayName = 'CreateRequestModal';
